import React, { useState, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
import commonColDef from '../../../data/grid/commonColDef';
import useGridOptionsSearchConditionOptions from '../../../hooks/grid/gridOptions/useGridOptionsSearchConditionOptions';
import useColdefConditionSearchDomesticNsc from '../../../hooks/grid/coldef/useColdefConditionSearchDomesticNsc';
import useColdefConditionSearchDomesticAichi from '../../../hooks/grid/coldef/useColdefConditionSearchDomesticAichi';
import useColdefConditionSearchDomesticKobe from '../../../hooks/grid/coldef/useColdefConditionSearchDomesticKobe';
import useColdefConditionSearchDomesticJfe from '../../../hooks/grid/coldef/useColdefConditionSearchDomesticJfe';
import useColdefConditionSearchExportNsc from '../../../hooks/grid/coldef/useColdefConditionSearchExportNsc';
import useColdefConditionSearchExportAichi from '../../../hooks/grid/coldef/useColdefConditionSearchExportAichi';
import useColdefConditionSearchExportKobe from '../../../hooks/grid/coldef/useColdefConditionSearchExportKobe';
import useColdefConditionSearchExportJfe from '../../../hooks/grid/coldef/useColdefConditionSearchExportJfe';
import SelectConditionOptionRenderer from '../../cellRenderers/SelectConditionOptionRenderer';
import { useTranslation } from 'react-i18next';
import { useDispatch } from '../../../redux/store';
import { applyConditionOptionsSelection, setSearchParams } from '../../../redux/slices/condition';
import _ from 'lodash';
import getOptionsRowNodeId from '../../../utils/manage-condition/getOptionsRowNodeId';
import { CONDITION_STATUS, MANAGE_CONDITION_TYPES, BUSINESS_TYPE, STEEL_MAKER } from '../../../constants';
import type { ColDef, ColumnApi, GridApi, GridReadyEvent, RowNode, RowSelectedEvent } from 'ag-grid-community';
import type { SteelMaker, BusinessType, SteelCategory } from '../../../types/common';
import type { ConditionWithDetailTypeClient, ConditionOptionsData, ConditionOptionsType, ConditionOptionsSearchParams } from '../../../types/condition';

interface GridConditionOptionsProps {
  isLoading: boolean;
  steelMaker: SteelMaker;
  businessType: BusinessType;
  steelCategory: SteelCategory;
  condition: ConditionWithDetailTypeClient;
  conditionOptions: ConditionOptionsType[];
  searchParams: ConditionOptionsSearchParams | undefined;
}

const GridConditionOptions = ({ isLoading, steelMaker, businessType, steelCategory, condition, conditionOptions, searchParams }: GridConditionOptionsProps): React.ReactElement => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [gridApi, setGridApi] = useState<GridApi | undefined>(undefined);
  const [gridColumnApi, setGridColumnApi] = useState<ColumnApi | undefined>(undefined);
  const { gridOptions } = useGridOptionsSearchConditionOptions();
  const customColDef: ColDef = {};
  const defaultColDef: ColDef = Object.assign({}, commonColDef, customColDef);
  const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
  // 前回の selectionChanged 以降にチェック状態が変わった行（rowNodeId → RowNode）
  const changedNodesRef = useRef<Map<string, RowNode>>(new Map());
  // detailCustomize からチェック状態を同期している最中かどうか
  const isSyncingSelectionRef = useRef<boolean>(false);
  const colDefDomesticNsc: ColDef[] = useColdefConditionSearchDomesticNsc(t);
  const colDefDomesticAichi: ColDef[] = useColdefConditionSearchDomesticAichi(t);
  const colDefDomesticJfe: ColDef[] = useColdefConditionSearchDomesticJfe(t);
  const colDefDomesticKobe: ColDef[] = useColdefConditionSearchDomesticKobe(t);
  const colDefExportNsc: ColDef[] = useColdefConditionSearchExportNsc(t);
  const colDefExportAichi: ColDef[] = useColdefConditionSearchExportAichi(t);
  const colDefExportKobe: ColDef[] = useColdefConditionSearchExportKobe(t);
  const colDefExportJfe: ColDef[] = useColdefConditionSearchExportJfe(t);

  // SIDE EFFECT
  // ================================================================================

  // 確認用
  // useEffect(() => {
  //   console.log(steelMaker, businessType, columnDefs);
  // }, [columnDefs]);

  /**
   * 受け取ったsteelMaker, businessTypeに応じてColDefを変更する
   */
  useEffect(() => {
    if (businessType === BUSINESS_TYPE.DOMESTIC) {
      // 国内
      if (steelMaker === STEEL_MAKER.NIPPONSTEEL) {
        setColumnDefs(colDefDomesticNsc);
      } else if (steelMaker === STEEL_MAKER.AICHISTEEL) {
        setColumnDefs(colDefDomesticAichi);
      } else if (steelMaker === STEEL_MAKER.KOBESTEEL) {
        setColumnDefs(colDefDomesticKobe);
      } else if (steelMaker === STEEL_MAKER.JFESTEEL) {
        setColumnDefs(colDefDomesticJfe);
      } else {
        setColumnDefs([]);
      }
    } else if (businessType === BUSINESS_TYPE.EXPORT) {
      // 輸出
      if (steelMaker === STEEL_MAKER.NIPPONSTEEL) {
        setColumnDefs(colDefExportNsc);
      } else if (steelMaker === STEEL_MAKER.AICHISTEEL) {
        setColumnDefs(colDefExportAichi);
      } else if (steelMaker === STEEL_MAKER.KOBESTEEL) {
        setColumnDefs(colDefExportKobe);
      } else if (steelMaker === STEEL_MAKER.JFESTEEL) {
        setColumnDefs(colDefExportJfe);
      } else {
        setColumnDefs([]);
      }
    } else {
      setColumnDefs([]);
    }
  }, [steelMaker, businessType]);

  /**
   * 受け取ったsteelMaker, businessType, steelCategoryに応じて検索パラメータを変更する
   */
  useEffect(() => {
    if (steelMaker && businessType && steelCategory) {
      const newSearchParams: ConditionOptionsSearchParams = Object.assign({}, searchParams, { steelMaker, businessType, steelCategory });
      dispatch(setSearchParams(newSearchParams));
    }
  }, [steelMaker, businessType, steelCategory]);

  /**
   * conditionが設定されたら、
   * それぞれのConditionOptionがConditionCustomizeにあるかどうか調べてチェック状態を決める
   */
  useEffect(() => {
    if (gridApi) {
      // 件数が多くても処理が重くならないよう、照合は rowNodeId の Set で行う
      const registeredRowNodeIds: Set<string> = new Set((condition?.detailCustomize ?? []).map((detail) => getOptionsRowNodeId(detail)));
      // 同期によるチェック状態の変化はユーザー操作ではないので、onRowSelected / onSelectionChanged で処理させない
      isSyncingSelectionRef.current = true;
      try {
        gridApi.forEachNode((node: RowNode) => {
          const data: ConditionOptionsType = node.data;
          node.setSelected(registeredRowNodeIds.has(getOptionsRowNodeId(data)));
        });
      } finally {
        isSyncingSelectionRef.current = false;
      }
    } else {
      // do nothing
    }
  }, [gridApi, condition?.detailCustomize, conditionOptions]);

  /**
   * 承認依頼中は選択チェックボックスをオフにする
   */
  useEffect(() => {
    // console.log('condition status changed');
    if (columnDefs.length) {
      const isRequested: boolean = condition?.conditionStatus === CONDITION_STATUS.REQUEST;
      const newColDef: ColDef[] = columnDefs.map((def) => {
        if (def.colId === 'selectBox') {
          let newDef: ColDef = _.cloneDeep(def);
          def.headerCheckboxSelection = () => !isRequested;
          def.checkboxSelection = () => !isRequested;
          return newDef;
        } else {
          return def;
        }
      });
      setColumnDefs(newColDef);
    }
  }, [condition?.conditionStatus]);

  /**
   * Loading, NoRowsのグリッド表示
   */
  useEffect(() => {
    if (gridApi) {
      if (isLoading) {
        gridApi.showLoadingOverlay();
      } else {
        if (conditionOptions?.length) {
          gridApi.hideOverlay();
        } else {
          gridApi.showNoRowsOverlay();
        }
      }
    } else {
      // do nothing
    }
  }, [gridApi, isLoading, conditionOptions]);

  // EVENT HANDLER
  // ================================================================================

  // 確認用
  // const handleCheck = (): void => {
  //   if (gridApi) {
  //     const hoge = gridApi.getSelectedRows();
  //     const fuga = gridApi.getDisplayedRowCount();
  //     console.log('selected rows: ', hoge);
  //     console.log('row count: ', fuga);
  //   }
  // };

  /**
   * グリッドが準備できたらAPIを利用できるようにする
   * @param params GridReadyEvent
   */
  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  /**
   * チェック状態が変わった行を記録する。詳細可視制御への反映は onSelectionChanged でまとめて行う
   *
   * 経緯: 以前はここで 1 行ずつ dispatch していたため、検索結果が 200 件を超えるような状態で
   * 全選択すると dispatch が大量に走りブラウザが固まった。その回避として「全選択・全解除のときは
   * 末尾行のイベントでのみ一括処理」としていたが、末尾行が操作前から選択済み（全解除時は未選択）
   * だと末尾行のイベントが来ず、追加・削除が漏れた（TM-264）。
   * そのため、ここでは記録だけを行い dispatch はしない。
   * @param params RowSelectedEvent
   */
  const onRowSelected = (params: RowSelectedEvent) => {
    if (isSyncingSelectionRef.current) {
      // do nothing
    } else {
      changedNodesRef.current.set(params.node.id, params.node);
    }
  };

  /**
   * 記録した行のチェック状態と詳細可視制御の差分を、1 回の dispatch でまとめて反映する
   *
   * selectionChanged は行を 1 件チェックしたときも、見出しで全選択・全解除したときも、
   * 操作 1 回につき最後に 1 回だけ発火する。そのため件数が多くても dispatch は 1 回で済む。
   */
  const onSelectionChanged = () => {
    if (isSyncingSelectionRef.current) {
      return;
    }
    const changedNodes: RowNode[] = Array.from(changedNodesRef.current.values());
    changedNodesRef.current.clear();
    if (condition && condition.conditionStatus !== CONDITION_STATUS.REQUEST && gridApi) {
      const registeredRowNodeIds: Set<string> = new Set(condition.detailCustomize.map((detail) => getOptionsRowNodeId(detail)));
      const toAdd: ConditionOptionsData[] = [];
      const toRemove: ConditionOptionsData[] = [];
      changedNodes.forEach((node: RowNode) => {
        // 検索し直して検索結果から外れた行も選択解除のイベントが来るが、ユーザー操作ではないので詳細可視制御は変えない
        if (gridApi.getRowNode(node.id) !== node) {
          return;
        }
        const data: ConditionOptionsType = node.data;
        const detail: ConditionOptionsData = {
          ...data,
          conditionId: condition.id,
          conditionType: MANAGE_CONDITION_TYPES.CUSTOMIZE,
        };
        const isRegistered: boolean = registeredRowNodeIds.has(getOptionsRowNodeId(data));
        if (node.isSelected() && !isRegistered) {
          // unchecked -> checkedの場合
          toAdd.push(detail);
        } else if (!node.isSelected() && isRegistered) {
          // checked -> uncheckedの場合
          toRemove.push(detail);
        } else {
          // do nothing
        }
      });
      if (toAdd.length || toRemove.length) {
        dispatch(applyConditionOptionsSelection({ toAdd, toRemove }));
      }
    } else {
      // do nothing
    }
  };

  // VIEW
  // ================================================================================

  return (
    <>
      <div id="searchConditionOptions" className="mb-4">
        <div className="ag-theme-balham" style={{ height: '50vh' }}>
          <AgGridReact
            immutableData={true}
            gridOptions={gridOptions}
            defaultColDef={defaultColDef}
            columnDefs={columnDefs}
            rowData={conditionOptions ?? []}
            onGridReady={onGridReady}
            onRowSelected={onRowSelected}
            onSelectionChanged={onSelectionChanged}
            frameworkComponents={{
              selectConditionOptionRenderer: SelectConditionOptionRenderer,
            }}
          />
        </div>
      </div>
    </>
  );
};

export default GridConditionOptions;
